import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import Logo from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, LogIn } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(email, password);
      if (u.must_change_password) {
        navigate("/change-password", { replace: true });
        return;
      }
      navigate(u.role === "courier" ? "/livreur" : u.role === "logistics_manager" ? "/orders" : "/dashboard", { replace: true });
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || "Connexion impossible");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      <div className="hidden lg:flex flex-1 brand-gradient relative overflow-hidden items-center justify-center p-12">
        <div
          className="absolute inset-0 opacity-20 bg-cover bg-center mix-blend-overlay"
          style={{ backgroundImage: `url(https://images.pexels.com/photos/8774411/pexels-photo-8774411.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)` }}
        />
        <div className="relative z-10 max-w-md">
          <Logo size={56} />
          <h1 className="text-white text-4xl font-extrabold font-heading mt-8 leading-tight">
            Gérez vos commandes de pressing, <span className="text-brand-yellow">du dépôt à la livraison.</span>
          </h1>
          <p className="text-brand-50 mt-4 text-lg">
            Collecte, traitement, livraison, paiement et reçus — le tout dans une seule application.
          </p>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6 bg-slate-50">
        <div className="w-full max-w-sm animate-fade-in">
          <div className="lg:hidden mb-8 flex justify-center">
            <Logo size={40} />
          </div>
          <h2 className="text-2xl font-bold font-heading text-slate-900">Connexion</h2>
          <p className="text-slate-500 mt-1 mb-6 text-sm">Accédez à votre espace CLINIT</p>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                data-testid="login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="vous@clinit.app"
                required
                className="mt-1.5"
              />
            </div>
            <div>
              <Label htmlFor="password">Mot de passe</Label>
              <Input
                id="password"
                data-testid="login-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                className="mt-1.5"
              />
            </div>
            {error && (
              <p data-testid="login-error" className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">
                {error}
              </p>
            )}
            <Button
              data-testid="login-submit"
              type="submit"
              disabled={loading}
              className="w-full bg-brand-600 hover:bg-brand-700 h-11"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (<><LogIn className="w-4 h-4 mr-2" /> Se connecter</>)}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <Link to="/commander" data-testid="public-order-link" className="text-sm text-brand-600 hover:underline">
              Passer une commande en ligne →
            </Link>
          </div>
          <p className="text-xs text-slate-400 mt-4 text-center">
            Mot de passe oublié ? Contactez votre administrateur.
          </p>
        </div>
      </div>
    </div>
  );
}
